import {
  useAccountUsers,
  useCreateUser,
  useDeleteUser,
  useFunctionalRoles,
  useReplaceUserSites,
  useReplaceUserTeams,
  useSitesWithMembers,
  useTeams,
  useUpdateUser,
} from '@my/directory-api/hooks';
import type {
  AppRoleSummary,
  AppRoleValue,
  SiteWithMembers,
  Team,
  User,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Globe, MapPin, Plus, Trash2, Users as UsersIcon, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UsersTabProps {
  /** Owning organisation id, resolved by the parent page from JWT claims. */
  organisationId: string;
}

/**
 * Users tab on the Settings → Users & Access page. Full CRUD on every
 * user with a Depot grant on the current organisation.
 *
 * Renders as a grid (mirrors the Tax Schemas pattern: dark slate header,
 * `divide-y divide-slate-500` body separators, capped scroll viewport so
 * mobile users can reach the horizontal scrollbar). New-user editor
 * renders at the top of the table as a colspan row; edits to existing
 * rows replace that row's cells with the same colspan editor.
 *
 * Five role / membership layers surface:
 *   - **Platform** badge (Owner / Admin / Manager / User)
 *   - **Functional** badges (purple, multi-select on edit)
 *   - **Derived** badges (italic slate, read-only — Driver today,
 *     populated when a Resource matches the user's email)
 *   - **Sites** (multi-select on edit; column shows first-few + overflow)
 *   - **Teams** (multi-select on edit; column shows first-few + overflow)
 *
 * Save flow on edit chains three calls when the relevant axes changed:
 * `PUT /users` → `PUT /users/{id}/sites` → `PUT /users/{id}/teams`.
 * On create, the same chain runs against the freshly-returned user id.
 *
 * BE rules surface as toasts:
 *   - Demoting / removing the last Owner is rejected.
 */
export function UsersTab({ organisationId }: UsersTabProps) {
  const { t } = useTranslation('organisation');

  const usersQuery = useAccountUsers();
  const rolesQuery = useFunctionalRoles('Check');
  const sitesQuery = useSitesWithMembers(organisationId);
  const teamsQuery = useTeams(organisationId, 'Check');
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();
  const replaceUserSites = useReplaceUserSites();
  const replaceUserTeams = useReplaceUserTeams();

  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<UserForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<UserRow | null>(null);

  // Scroll the editor row into view whenever an edit/add starts. Without
  // this, clicking the pencil on a row at the bottom of the grid leaves
  // the editor below the viewport on small screens. Same trick used by
  // Tax Schemas.
  const editorRowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (editingId === null) return;
    const id = requestAnimationFrame(() => {
      editorRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [editingId]);

  const isBusy =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    replaceUserSites.isPending ||
    replaceUserTeams.isPending;

  // Catalogue rows the picker offers — derived ones (Driver) hidden;
  // the BE rejects them on write anyway.
  const assignableRoles = useMemo(
    () => (rolesQuery.data ?? []).filter((r) => !r.isDerived),
    [rolesQuery.data],
  );

  // Stable references via useMemo so downstream useMemo deps don't
  // churn — `?? []` creates a fresh empty array on every render and
  // would invalidate the lookup-map memos for free.
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const teams = useMemo(() => teamsQuery.data ?? [], [teamsQuery.data]);

  // Per-user lookup tables built once from the org-level queries above.
  // `sitesByUserId` and `teamsByUserId` give the row projector cheap
  // access to "what sites is Bob on?" / "what teams is Bob in?" without
  // per-user round-trips. Inverted from the (site → members) and
  // (team → members) shapes the BE returns.
  const sitesByUserId = useMemo(() => {
    const map = new Map<string, SiteWithMembers[]>();
    for (const site of sites) {
      for (const m of site.members) {
        const list = map.get(m.userId) ?? [];
        list.push(site);
        map.set(m.userId, list);
      }
    }
    return map;
  }, [sites]);

  const teamsByUserId = useMemo(() => {
    const map = new Map<string, Team[]>();
    for (const team of teams) {
      for (const m of team.members) {
        const list = map.get(m.userId) ?? [];
        list.push(team);
        map.set(m.userId, list);
      }
    }
    return map;
  }, [teams]);

  const rows = useMemo<UserRow[]>(() => {
    if (!usersQuery.data) return [];
    return usersQuery.data
      .map((u) => deriveUserRow(u, organisationId, sitesByUserId, teamsByUserId))
      .filter((r): r is UserRow => r !== null)
      .sort((a, b) => {
        const rank = (role: AppRoleValue) => PLATFORM_ROLE_ORDER.indexOf(role);
        if (rank(a.role) !== rank(b.role)) return rank(a.role) - rank(b.role);
        return a.fullName.localeCompare(b.fullName);
      });
  }, [usersQuery.data, organisationId, sitesByUserId, teamsByUserId]);

  // Count of Owners on THIS org (Depot). Drives the FE last-Owner guard
  // for both the editor (block demotion) and the trash button (block
  // removal). The BE enforces the same rule at the handler — this is a
  // UX preflight so the user gets immediate feedback rather than seeing
  // a toast after a round-trip.
  const ownerCount = useMemo(
    () => rows.filter((r) => r.role === 'Owner').length,
    [rows],
  );

  /** The row currently being edited, if any. */
  const editingRow = useMemo(
    () =>
      typeof editingId === 'string' && editingId !== 'new'
        ? (rows.find((r) => r.id === editingId) ?? null)
        : null,
    [editingId, rows],
  );

  const startEdit = (row: UserRow) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft, editingId === 'new')) return;
    if (
      editingRow &&
      editingRow.role === 'Owner' &&
      draft.role !== 'Owner' &&
      ownerCount === 1
    ) {
      return;
    }

    try {
      let userId: string;
      if (editingId === 'new') {
        userId = await createMutation.mutateAsync({
          organisationId,
          email: draft.email.trim().toLowerCase(),
          firstName: draft.firstName.trim() || null,
          lastName: draft.lastName.trim() || null,
          mobileNumber: draft.mobileNumber.trim() || null,
          role: draft.role,
          functionalRoles: draft.functionalRoles,
        });
        showToast({ type: 'success', message: t('users.added') });
      } else {
        userId = editingId;
        await updateMutation.mutateAsync({
          userId,
          organisationId,
          firstName: draft.firstName.trim() || null,
          lastName: draft.lastName.trim() || null,
          mobileNumber: draft.mobileNumber.trim() || null,
          role: draft.role,
          functionalRoles: draft.functionalRoles,
        });
        showToast({ type: 'success', message: t('users.updated') });
      }

      // Chain Sites + Teams replace calls. For Create, only fire when
      // the admin actually picked anything. For Update, only fire when
      // the set differs from the current state — saves a round-trip on
      // a no-op edit. The handlers diff internally, so passing the
      // unchanged set would also be safe, just wasteful.
      const shouldWriteSites =
        editingId === 'new'
          ? draft.siteIds.length > 0
          : !setEquals(editingRow?.siteIds ?? [], draft.siteIds);
      if (shouldWriteSites) {
        await replaceUserSites.mutateAsync({
          userId,
          organisationId,
          body: { siteIds: [...draft.siteIds] },
        });
      }

      const shouldWriteTeams =
        editingId === 'new'
          ? draft.teamIds.length > 0
          : !setEquals(editingRow?.teamIds ?? [], draft.teamIds);
      if (shouldWriteTeams) {
        await replaceUserTeams.mutateAsync({
          userId,
          body: {
            organisationId,
            app: 'Check',
            teamIds: [...draft.teamIds],
          },
        });
      }

      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('users.saveFailed'),
      });
    }
  };

  const requestDelete = (row: UserRow) => {
    if (row.role === 'Owner' && ownerCount === 1) return;
    setPendingDelete(row);
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row) return;
    try {
      await deleteMutation.mutateAsync({ id: row.id, organisationId });
      showToast({ type: 'success', message: t('users.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('users.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const COLSPAN = 8;

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4">
      {/* Title is intentionally omitted — the active sub-nav entry on the
          left already names the section. The description sits inline with
          the action area so the header reads as one row of context +
          actions, matching the Teams / Sites tab convention. */}
      <header className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">{t('users.description')}</p>
        {usersQuery.isSuccess && editingId === null && (
          <button
            type="button"
            aria-label={t('users.addAria')}
            onClick={startNew}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('users.add')}
          </button>
        )}
      </header>

      {usersQuery.isPending && <Loading label={t('users.loading')} />}
      {usersQuery.isError && (
        <ErrorBanner title={t('users.loadFailed')} message={usersQuery.error.message} />
      )}

      {usersQuery.isSuccess && (
        <div className="overflow-hidden rounded-md">
          {/*
            View mode: cap height + inner scroll so a long list of users
            doesn't push the rest of the page down. Edit mode: drop the
            cap so the inline editor row (which expands to fit the
            address form, member picker, etc.) renders in full without a
            scrollbar inside the panel. Same fix pattern as the Teams +
            Sites tabs.
          */}
          <div
            className={
              'overflow-x-auto ' +
              (editingId === null ? 'max-h-[60vh] overflow-y-auto' : '')
            }
          >
            <table className="w-full min-w-[1080px] table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.user')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.email')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.mobile')}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold">
                    {t('users.table.platform')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.functionalRoles')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.sites')}
                  </th>
                  <th className="px-3 py-2 text-left font-semibold">
                    {t('users.table.teams')}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t('users.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500">
                {editingId === 'new' && (
                  <tr ref={editorRowRef}>
                    <td colSpan={COLSPAN} className="bg-slate-50 p-3">
                      <div className="max-w-2xl">
                        <UserEditor
                          draft={draft}
                          onChange={setDraft}
                          submitted={submitted}
                          isNew
                          assignableRoles={assignableRoles}
                          siteOptions={sites}
                          teamOptions={teams}
                          onSave={handleSave}
                          onCancel={cancel}
                          isBusy={isBusy}
                        />
                      </div>
                    </td>
                  </tr>
                )}

                {rows.length === 0 && editingId !== 'new' && (
                  <tr>
                    <td
                      colSpan={COLSPAN}
                      className="px-3 py-3 text-center text-sm text-slate-500"
                    >
                      {t('users.empty')}
                    </td>
                  </tr>
                )}

                {rows.map((row) => {
                  const isLastOwner = row.role === 'Owner' && ownerCount === 1;
                  if (editingId === row.id) {
                    return (
                      <tr key={row.id} ref={editorRowRef}>
                        <td colSpan={COLSPAN} className="bg-slate-50 p-3">
                          <div className="max-w-2xl">
                            <UserEditor
                              draft={draft}
                              onChange={setDraft}
                              submitted={submitted}
                              isNew={false}
                              assignableRoles={assignableRoles}
                              siteOptions={sites}
                              teamOptions={teams}
                              originalRole={row.role}
                              isLastOwner={isLastOwner}
                              onSave={handleSave}
                              onCancel={cancel}
                              onDelete={
                                isLastOwner ? undefined : () => requestDelete(row)
                              }
                              isBusy={isBusy}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const otherEditing = editingId !== null;
                  return (
                    <tr
                      key={row.id}
                      className={
                        otherEditing ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'
                      }
                      onDoubleClick={otherEditing ? undefined : () => startEdit(row)}
                    >
                      <td className="px-3 py-1 text-slate-900">
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-semibold text-slate-700"
                            aria-hidden
                          >
                            {row.initials}
                          </span>
                          <span className="truncate font-medium">{row.fullName}</span>
                        </div>
                      </td>
                      <td className="px-3 py-1 text-slate-700">
                        <span className="block max-w-[220px] truncate">{row.email}</span>
                      </td>
                      <td className="px-3 py-1 text-slate-700">
                        {row.mobileNumber || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-1 text-center">
                        <PlatformBadge role={row.role} />
                      </td>
                      <td className="px-3 py-1">
                        <FunctionalRolesText
                          assigned={row.functionalRoles}
                          derived={row.derivedFunctionalRoles}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <NamesPreview
                          names={row.siteNames}
                          icon={<MapPin className="h-3 w-3" />}
                          tone="bg-blue-50 text-blue-700 ring-blue-200"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <NamesPreview
                          names={row.teamNames}
                          icon={<UsersIcon className="h-3 w-3" />}
                          tone="bg-purple-50 text-purple-700 ring-purple-200"
                        />
                      </td>
                      <td className="px-3 py-1 text-right">
                        <button
                          type="button"
                          aria-label={t('users.ariaEdit', { name: row.fullName })}
                          onClick={() => startEdit(row)}
                          disabled={otherEditing}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('users.deleteDialog.title')}
        message={
          pendingDelete
            ? t('users.deleteDialog.message', { name: pendingDelete.fullName })
            : null
        }
        confirmLabel={t('users.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function setEquals(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const x of b) if (!set.has(x)) return false;
  return true;
}

// ────────────────────────────────────────────────────────────────────────────
// Row projection
// ────────────────────────────────────────────────────────────────────────────

const PLATFORM_ROLE_ORDER: AppRoleValue[] = [
  'Owner',
  'Admin',
  'Manager',
  'User',
];

interface UserRow {
  id: string;
  fullName: string;
  email: string;
  initials: string;
  mobileNumber: string;
  role: AppRoleValue;
  functionalRoles: string[];
  derivedFunctionalRoles: string[];
  /** Used by `toForm` to pre-fill the editor. */
  firstName: string;
  lastName: string;
  /** Sites the user is assigned to (cross-app). */
  siteIds: string[];
  siteNames: string[];
  /** Teams the user is on for THIS org/app. */
  teamIds: string[];
  teamNames: string[];
}

function deriveUserRow(
  user: User,
  organisationId: string,
  sitesByUserId: Map<string, SiteWithMembers[]>,
  teamsByUserId: Map<string, Team[]>,
): UserRow | null {
  let grant: AppRoleSummary | null = null;
  for (const account of user.accounts) {
    if (account.status === 'Suspended' || account.status === 'Left') continue;
    for (const r of account.appRoles) {
      if (r.app !== 'Check') continue;
      if (r.organisationId !== organisationId) continue;
      grant = r;
      break;
    }
    if (grant) break;
  }
  if (!grant) return null;

  const userSites = sitesByUserId.get(user.id) ?? [];
  // Filter teams to those in THIS org. The teams list passed in is
  // already org-and-app scoped (via useTeams(orgId, 'Check')), but
  // double-check defensively.
  const userTeams = (teamsByUserId.get(user.id) ?? []).filter(
    (t) => t.organisationId === organisationId,
  );

  return {
    id: user.id,
    fullName: user.fullName || user.email,
    email: user.email,
    initials: user.initials || initialsFallback(user.email),
    mobileNumber: user.mobileNumber ?? '',
    role: grant.role,
    functionalRoles: [...grant.functionalRoles],
    derivedFunctionalRoles: [...grant.derivedFunctionalRoles],
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    siteIds: userSites.map((s) => s.id),
    siteNames: userSites.map((s) => s.name),
    teamIds: userTeams.map((tm) => tm.id),
    teamNames: userTeams.map((tm) => tm.name),
  };
}

function initialsFallback(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

// ────────────────────────────────────────────────────────────────────────────
// Editor form
// ────────────────────────────────────────────────────────────────────────────

interface UserForm {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  role: AppRoleValue;
  functionalRoles: string[];
  siteIds: string[];
  teamIds: string[];
}

function emptyForm(): UserForm {
  return {
    email: '',
    firstName: '',
    lastName: '',
    mobileNumber: '',
    role: 'User',
    functionalRoles: [],
    siteIds: [],
    teamIds: [],
  };
}

function toForm(row: UserRow): UserForm {
  return {
    email: row.email,
    firstName: row.firstName,
    lastName: row.lastName,
    mobileNumber: row.mobileNumber,
    role: row.role,
    functionalRoles: [...row.functionalRoles],
    siteIds: [...row.siteIds],
    teamIds: [...row.teamIds],
  };
}

function isFormValid(form: UserForm, isNew: boolean): boolean {
  if (isNew) {
    if (!form.email.trim()) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return false;
  }
  if (form.mobileNumber.length > 32) return false;
  return true;
}

interface UserEditorProps {
  draft: UserForm;
  onChange: (next: UserForm) => void;
  submitted: boolean;
  isNew: boolean;
  assignableRoles: { key: string; displayOrder: number }[];
  siteOptions: { id: string; name: string }[];
  teamOptions: { id: string; name: string; siteId?: string | null; siteName?: string | null }[];
  /** The role this user had before the edit started, if editing. */
  originalRole?: AppRoleValue;
  /** True when the user being edited is the only Owner on this org-app. */
  isLastOwner?: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** Omit to hide the trash button — used to lock delete on the last Owner. */
  onDelete?: () => void;
  isBusy: boolean;
}

function UserEditor({
  draft,
  onChange,
  submitted,
  isNew,
  assignableRoles,
  siteOptions,
  teamOptions,
  originalRole,
  isLastOwner = false,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: UserEditorProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof UserForm>(key: K, value: UserForm[K]) =>
    onChange({ ...draft, [key]: value });

  const sortedRoles = [...assignableRoles].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const wouldDemoteLastOwner =
    !isNew && isLastOwner && originalRole === 'Owner' && draft.role !== 'Owner';

  const toggleFunctional = (key: string) => {
    set(
      'functionalRoles',
      draft.functionalRoles.includes(key)
        ? draft.functionalRoles.filter((k) => k !== key)
        : [...draft.functionalRoles, key],
    );
  };

  const toggleSite = (id: string) => {
    set(
      'siteIds',
      draft.siteIds.includes(id)
        ? draft.siteIds.filter((x) => x !== id)
        : [...draft.siteIds, id],
    );
  };

  const toggleTeam = (id: string) => {
    set(
      'teamIds',
      draft.teamIds.includes(id)
        ? draft.teamIds.filter((x) => x !== id)
        : [...draft.teamIds, id],
    );
  };

  const errs = {
    email:
      isNew &&
      (draft.email.trim().length === 0 ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())),
  };

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
      <div className="space-y-3">
        <TextInput
          label={t('users.fields.email')}
          name="email"
          type="email"
          autoComplete="off"
          value={draft.email}
          maxLength={256}
          disabled={!isNew}
          onChange={(e) => set('email', e.target.value)}
          error={submitted && errs.email}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label={t('users.fields.firstName')}
            name="firstName"
            autoComplete="off"
            value={draft.firstName}
            maxLength={128}
            onChange={(e) => set('firstName', e.target.value)}
          />
          <TextInput
            label={t('users.fields.lastName')}
            name="lastName"
            autoComplete="off"
            value={draft.lastName}
            maxLength={128}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </div>
        <TextInput
          label={t('users.fields.mobileNumber')}
          name="mobileNumber"
          type="tel"
          autoComplete="off"
          value={draft.mobileNumber}
          maxLength={32}
          placeholder={t('users.fields.mobileNumberPlaceholder')}
          onChange={(e) => set('mobileNumber', e.target.value)}
        />
        <div>
          <SelectInput
            label={t('users.fields.platformRole')}
            name="role"
            value={draft.role}
            onChange={(e) => set('role', e.target.value as AppRoleValue)}
            options={PLATFORM_ROLE_ORDER.map((r) => ({
              value: r,
              label: t(`roles.platformRoles.${r}`, { defaultValue: r }),
            }))}
          />
          {wouldDemoteLastOwner && (
            <p className="mt-1 text-xs text-red-600">
              {t('users.validation.cannotDemoteLastOwner')}
            </p>
          )}
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-xs font-medium text-slate-700">
            {t('users.fields.functionalRoles')}
          </legend>
          {sortedRoles.length === 0 ? (
            <p className="text-xs text-slate-500">
              {t('users.fields.noFunctionalRoles')}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
              {sortedRoles.map((r) => {
                const checked = draft.functionalRoles.includes(r.key);
                return (
                  <label
                    key={r.key}
                    className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFunctional(r.key)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    {t(`roles.functionalRoles.${r.key}`, { defaultValue: r.key })}
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className="text-xs font-medium text-slate-700">
            {t('users.fields.sites')}
          </legend>
          {siteOptions.length === 0 ? (
            <p className="text-xs text-slate-500">{t('users.fields.noSitesYet')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
              {siteOptions.map((s) => {
                const checked = draft.siteIds.includes(s.id);
                return (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSite(s.id)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="truncate">{s.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className="text-xs font-medium text-slate-700">
            {t('users.fields.teams')}
          </legend>
          {teamOptions.length === 0 ? (
            <p className="text-xs text-slate-500">{t('users.fields.noTeamsYet')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
              {teamOptions.map((tm) => {
                const checked = draft.teamIds.includes(tm.id);
                return (
                  <label
                    key={tm.id}
                    className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTeam(tm.id)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="truncate">
                      {tm.name}
                      {tm.siteName && (
                        <span className="ml-1 text-[10px] text-slate-500">
                          · {tm.siteName}
                        </span>
                      )}
                      {!tm.siteId && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] italic text-slate-500">
                          <Globe className="h-2.5 w-2.5" />
                          {t('teams.crossSiteShort')}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete ? (
            <button
              type="button"
              aria-label={t('users.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : isLastOwner ? (
            <button
              type="button"
              aria-label={t('users.ariaDelete')}
              disabled
              title={t('users.validation.cannotRemoveLastOwner')}
              className="flex h-7 w-7 cursor-not-allowed items-center justify-center rounded-sm text-red-600 opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('users.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('users.ariaSave')}
            onClick={onSave}
            disabled={isBusy || wouldDemoteLastOwner}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Badges + previews
// ────────────────────────────────────────────────────────────────────────────

function PlatformBadge({ role }: { role: AppRoleValue }) {
  const { t } = useTranslation('organisation');
  const tone =
    role === 'Owner'
      ? 'bg-blue-50 text-blue-700 ring-blue-200'
      : role === 'Admin'
        ? 'bg-purple-50 text-purple-700 ring-purple-200'
        : 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone}`}
    >
      {t(`roles.platformRoles.${role}`, { defaultValue: role })}
    </span>
  );
}

/**
 * Functional roles cell — plain italic text, comma-separated. Both the
 * operator-assigned roles and any derived roles (e.g. Driver, populated
 * automatically when a Resource matches the user's email) flow through
 * the same string. Derived roles are tooltipped via `title` so the
 * "why is this here?" question is one hover away. Renders the em-dash
 * placeholder when nothing applies.
 */
function FunctionalRolesText({
  assigned,
  derived,
}: {
  assigned: string[];
  derived: string[];
}) {
  const { t } = useTranslation('organisation');
  if (assigned.length === 0 && derived.length === 0) {
    return <span className="text-slate-400">—</span>;
  }
  const labels = [...assigned, ...derived].map((key) =>
    t(`roles.functionalRoles.${key}`, { defaultValue: key }),
  );
  return (
    <span
      className="text-[10px] italic text-slate-600"
      title={derived.length > 0 ? t('users.fields.derivedHint') : undefined}
    >
      {labels.join(', ')}
    </span>
  );
}

/**
 * Compact "first 1-2 names + overflow" cell for grid columns showing a
 * variable-length list (Sites, Teams). Hover reveals the full list via
 * the title attribute. Same UX as Slack / Linear's reaction lists.
 */
function NamesPreview({
  names,
  icon,
  tone,
}: {
  names: string[];
  icon: React.ReactNode;
  tone: string;
}) {
  if (names.length === 0) return <span className="text-slate-400">—</span>;
  const previewCount = 1;
  const head = names.slice(0, previewCount);
  const overflow = names.length - previewCount;
  const title = names.join(', ');
  return (
    <div className="flex flex-wrap items-center gap-1" title={title}>
      {head.map((name) => (
        <span
          key={name}
          className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${tone}`}
        >
          {icon}
          <span className="max-w-[100px] truncate">{name}</span>
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] text-slate-500">+{overflow}</span>
      )}
    </div>
  );
}
